import { IGetCommentResponse } from "../../../api/types"
import { CommentData, ReplyData } from "../../../types"
import { DELETED_USER_LABEL, isDeletedUser } from "../../../utils/deletedUserDisplay"

const mapLikedByMe = (node: IGetCommentResponse): boolean => (node.my_reaction ?? 0) === 1

const buildInReplyToLine = (reply: IGetCommentResponse): string | undefined => {
    if (reply.reply_to_comment_id == null || reply.reply_to == null) return undefined
    const { author_first_name, author_last_name, text_preview } = reply.reply_to
    const name = [author_first_name, author_last_name].filter(Boolean).join(" ").trim()
    const head = name.length > 0 ? `В ответ ${name}` : "В ответ"
    const prev = (text_preview ?? "").trim()
    if (prev.length > 0) return `${head}: ${prev}`
    return head
}

export const getNames = (author?: IGetCommentResponse["author"]) => {
    if (isDeletedUser(author)) {
        return { firstName: DELETED_USER_LABEL, lastName: "" }
    }

    const source = author?.shortname || author?.fullname || ""
    const [firstName = "", ...rest] = source.split(" ").filter(Boolean)
    return { firstName, lastName: rest.join(" ") }
}

/** Рекурсивно: прямой родитель в UI — immediateParentId (для POST parent_id). */
export const mapApiReplyToUi = (
    reply: IGetCommentResponse,
    immediateParentId: number,
    opts?: { stripThreadSubtree?: boolean }
): ReplyData => {
    const stripSubtree = opts?.stripThreadSubtree ?? false
    const { firstName, lastName } = getNames(reply.author)
    const childNodes = stripSubtree ? [] : (reply.children ?? [])
    const mappedNested = childNodes.map((child) => mapApiReplyToUi(child, reply.id))
    const shown = childNodes.length
    const total = reply.counters?.children ?? 0
    const hasMore = stripSubtree ? false : total > shown

    return {
        id: reply.id,
        commentId: immediateParentId,
        authorId: reply.author?.id ?? 0,
        firstName,
        lastName,
        avatarUrl: isDeletedUser(reply.author) ? "" : (reply.author?.avatar ?? ""),
        text: reply.text,
        date: reply.published_at ?? "",
        likes: reply.counters?.likes ?? 0,
        likedByMe: mapLikedByMe(reply),
        isUpdated: reply.edited,
        canDelete: reply.viewer?.can_delete === true,
        inReplyToLine: buildInReplyToLine(reply),
        ...(stripSubtree ? { threadChildrenCount: total } : {}),
        replyTo: {
            userId: 0,
            firstName: "",
            lastName: "",
        },
        replies: mappedNested.length ? mappedNested : undefined,
        hasMoreChildren: hasMore,
    }
}

/** Одна строка списка в модалке треда под L2: без вложенных children. */
export const mapApiNodeToFlatThreadReply = (reply: IGetCommentResponse): ReplyData => {
    const { firstName, lastName } = getNames(reply.author)
    return {
        id: reply.id,
        commentId: reply.parent_id ?? 0,
        authorId: reply.author?.id ?? 0,
        firstName,
        lastName,
        avatarUrl: isDeletedUser(reply.author) ? "" : (reply.author?.avatar ?? ""),
        text: reply.text,
        date: reply.published_at ?? "",
        likes: reply.counters?.likes ?? 0,
        likedByMe: mapLikedByMe(reply),
        isUpdated: reply.edited,
        canDelete: reply.viewer?.can_delete === true,
        inReplyToLine: buildInReplyToLine(reply),
        replyTo: {
            userId: 0,
            firstName: "",
            lastName: "",
        },
        replies: undefined,
        hasMoreChildren: false,
    }
}

/** @deprecated используйте mapApiReplyToUi */
export const mapReply = (reply: IGetCommentResponse, commentId: number): ReplyData =>
    mapApiReplyToUi(reply, commentId, { stripThreadSubtree: true })

export const mapApiCommentToUiComment = (comment: IGetCommentResponse): CommentData => {
    const { firstName, lastName } = getNames(comment.author)
    const childNodes = comment.children ?? []
    const replies = childNodes.map((child) => mapApiReplyToUi(child, comment.id, { stripThreadSubtree: true }))
    const shown = childNodes.length
    const total = comment.counters?.children ?? 0

    return {
        id: comment.id,
        authorId: comment.author?.id ?? 0,
        firstName,
        lastName,
        avatarUrl: isDeletedUser(comment.author) ? "" : (comment.author?.avatar ?? ""),
        text: comment.text,
        date: comment.published_at ?? "",
        likes: comment.counters?.likes ?? 0,
        likedByMe: mapLikedByMe(comment),
        isUpdated: comment.edited,
        canDelete: comment.viewer?.can_delete === true,
        replies: replies.length ? replies : undefined,
        hasMoreChildren: total > shown,
    }
}

export const mapApiCommentsToUiComments = (comments: IGetCommentResponse[]): CommentData[] =>
    comments.map(mapApiCommentToUiComment)
